import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SEO from '../components/SEO';
import useCMSContent from '../hooks/useCMSContent';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PortfolioPage = () => {
  const cms = useCMSContent('portfolio');
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => {
    fetchPhotos();
    fetchVideos();
  }, []);

  const fetchPhotos = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/media/portfolio`);
      setPhotos(response.data);
    } catch (error) {
      console.error('Error fetching photos:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/videos`);
      setVideos(response.data.videos || []);
    } catch (error) {
      console.error('Error fetching videos:', error);
    }
  };

  const extractYouTubeId = (url) => {
    const match = url?.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
  };

  const openLightbox = (index) => {
    setCurrentImage(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  };

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % photos.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + photos.length) % photos.length);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxOpen) {
        if (e.key === 'Escape') closeLightbox();
        if (e.key === 'ArrowRight') nextImage();
        if (e.key === 'ArrowLeft') prevImage();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, currentImage]);

  const seoData = {
    title: 'Portfolio - Connecticut Home Remodeling Projects | Visser Construction',
    description: 'View our completed home remodeling projects in Connecticut. Kitchen renovations, bathroom remodels, decks, basements, and more. Quality craftsmanship gallery.',
    keywords: 'Connecticut home remodeling portfolio, CT renovation photos, before and after remodeling Connecticut, construction project gallery',
    canonical: 'https://cthomeremodels.com/portfolio',
    ogImage: 'https://cthomeremodels.com/og/v2/portfolio.png'
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="portfolio-page">
      <SEO {...seoData} />
      
      {/* Hero */}
      <section 
        className="relative py-32 md:py-40 bg-cover bg-center bg-no-repeat" 
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.5)), url('/api/media/name/composite-deck-white-railing-connecticut-home-remodeling.jpg')`
        }}
      >
        <div className="absolute top-8 left-8">
          <img 
            src="/api/media/name/visser-general-construction-llc-logo-connecticut.png"
            alt="Visser General Construction LLC Logo"
            className="h-24 md:h-32 object-contain drop-shadow-2xl"
          />
        </div>
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white drop-shadow-lg">{cms('hero_title', 'Our Portfolio')}</h1>
          <p className="text-xl max-w-3xl mx-auto text-white drop-shadow-md">
            {cms('hero_subtitle', 'Explore our completed projects showcasing quality craftsmanship and attention to detail')}
          </p>
        </div>
      </section>

      {/* Project Videos */}
      {videos.length > 0 && (
        <section className="py-16 bg-gray-50" data-testid="portfolio-videos">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-8">Project Videos</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {videos.map((video, idx) => {
                const ytId = extractYouTubeId(video.youtube_url);
                if (!ytId) return null;
                return (
                  <div key={idx} className="bg-white rounded-xl overflow-hidden shadow-md" data-testid={`portfolio-video-${idx}`}>
                    <div className="relative pb-[56.25%]">
                      <iframe
                        src={`https://www.youtube.com/embed/${ytId}`}
                        title={video.title}
                        className="absolute inset-0 w-full h-full"
                        allowFullScreen
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-sm">{video.title}</h3>
                      {video.caption && <p className="text-xs text-gray-500 mt-1">{video.caption}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Gallery */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="mb-8 text-center">
            <p className="text-gray-600">
              Showing {photos.length} project photos • Click any image to view larger
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {photos.map((photo, index) => (
              <div
                key={index}
                className="relative aspect-square overflow-hidden rounded-lg cursor-pointer group"
                onClick={() => openLightbox(index)}
                data-testid={`portfolio-image-${index}`}
              >
                <img
                  src={photo.url}
                  alt={photo.alt}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                  <svg className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white text-4xl hover:text-gray-300 z-10"
            data-testid="lightbox-close"
          >
            ×
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              prevImage();
            }}
            className="absolute left-4 text-white text-5xl hover:text-gray-300 z-10"
            data-testid="lightbox-prev"
          >
            ‹
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              nextImage();
            }}
            className="absolute right-4 text-white text-5xl hover:text-gray-300 z-10"
            data-testid="lightbox-next"
          >
            ›
          </button>

          <div className="max-w-7xl max-h-[90vh] px-4" onClick={(e) => e.stopPropagation()}>
            <img
              src={photos[currentImage].url}
              alt={photos[currentImage].alt}
              className="max-w-full max-h-[90vh] object-contain"
              data-testid="lightbox-image"
            />
            <div className="text-white text-center mt-4">
              <p className="text-sm">
                {currentImage + 1} / {photos.length}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* CTA */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">{cms('cta_title', 'Let\'s Build Your Dream Project')}</h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            {cms('cta_subtitle', 'Ready to start your home transformation? Contact us today')}
          </p>
          <a
            href="/contact"
            className="inline-block bg-[#3a87fe] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#2968d9] transition"
            data-testid="portfolio-cta-contact"
          >
            Get Started
          </a>
        </div>
      </section>
    </div>
  );
};

export default PortfolioPage;